<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { PhoneCode, codes } from '~/entities/phone-code';

const { code = '+7', number: number, note, isInvalidFormat } = defineProps<{
  code: string;
  number: string;
  phoneNumber: string;
  note?: string;
  isInvalidFormat?: boolean;
}>();

type CodeOption = Pick<PhoneCode, 'code'>;

const baseInputRef = ref();
const numberModel = ref(number);

const emit = defineEmits(['update:code', 'update:number', 'update:phoneNumber']);

const codeModel = computed({
  get: () => code,
  set: (value) => {
    emit('update:code', value);
    emit('update:phoneNumber', value + numberModel.value);
  },
});

const onNumberInput = (event: any) => {
  numberModel.value = event.target.value.replace(/\D/g, '');

  emit('update:number', numberModel.value);
  emit('update:phoneNumber', codeModel.value + numberModel.value);
};

const { t } = useI18n();
const phoneError = computed(() => (isInvalidFormat ? t('auth.invalidFormat') : ''));
</script>

<template>
  <div class="wrapper">
    <BaseSelect
      v-model="codeModel"
      :label="$t('auth.code')"
      :options="codes"
      :label-getter="(option: CodeOption) => option.code"
      :value-getter="(option: CodeOption) => option.code"
      class="code"
      tabindex="1"
    />

    <div class="input-wrapper">
      <BaseInput
        ref="baseInputRef"
        v-model="numberModel"
        @input="onNumberInput"
        :title="$t('auth.number')"
        :note="note"
        :focus="true"
        :error="phoneError"
        mode="tel"
        class="input-number"
        tabindex="2"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  gap: $items-spacing;

  :deep(.code) {
    .current-option {
      gap: 25px;

      &__text {
        white-space: nowrap;
      }

      svg {
        margin-left: 0;
      }
    }
  }

  .input-wrapper {
    width: 100%;
  }
}
</style>
